import {
  MutationFunction,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query'

import { fetchOnClient } from '@/utils/fetcher/fetcher.client'
import { ENDPOINT, fetchGetBlob } from '@/utils/fetcher/fetcher.helpers'

import { ConfigCacheOneDay } from '@/configs/constants'

import {
  DataIndukFiltersResponse,
  DistrictAncestorsResponse,
  MasterSatdikResponse,
  SatpenListResponse,
  SatpenPtkSummaryResponse,
  SatpenStatsByParentWilayahResponse,
  SatpenStatsResponse,
} from './types'

export interface DataIndukFiltersParams {
  bentuk: string
  jalur: string
  status: string
  jenis: string
}

export interface DataIndukFiltersWithKeywordParams
  extends DataIndukFiltersParams {
  keyword?: string
  kodeKecamatan?: string
  kodeKabupaten?: string
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export interface DataIndukDownloadParams
  extends DataIndukFiltersWithKeywordParams {
  format?: 'csv' | 'xlsx' | 'pdf'
}

export const useFetchMasterSatdikDetail = (
  npsn: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<MasterSatdikResponse, Error> => {
  return useQuery<MasterSatdikResponse, Error>({
    queryKey: [`satpen-detail`, npsn],
    queryFn: async (): Promise<MasterSatdikResponse> =>
      fetchOnClient<MasterSatdikResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/details/${npsn}`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useFetchDetailPtkSummary = (
  npsn: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<SatpenPtkSummaryResponse, Error> => {
  return useQuery<SatpenPtkSummaryResponse, Error>({
    queryKey: [`ptk-detail`, npsn],
    queryFn: async (): Promise<SatpenPtkSummaryResponse> =>
      fetchOnClient<SatpenPtkSummaryResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/details/${npsn}/ptk-summary`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export function generateDataIndukQueryString(
  filters: DataIndukFiltersWithKeywordParams
) {
  const searchParams = new URLSearchParams()
  if (filters?.kodeKecamatan) {
    searchParams.set('kodeKecamatan', filters?.kodeKecamatan)
  }
  if (filters?.kodeKabupaten) {
    searchParams.set('kodeKabupaten', filters?.kodeKabupaten)
  }
  if (filters?.keyword) {
    searchParams.set('keyword', filters?.keyword)
  }
  if (filters?.jalur) {
    searchParams.set('jalurPendidikan', filters?.jalur)
  }
  if (filters?.bentuk) {
    searchParams.set('bentukPendidikan', filters?.bentuk)
  }
  if (filters?.status) {
    searchParams.set('statusSatuanPendidikan', filters?.status)
  }
  if (filters?.jenis) {
    searchParams.set('jenisPendidikan', filters?.jenis)
  }

  searchParams.set('sortBy', filters?.sortBy || 'bentuk_pendidikan')
  searchParams.set('sortDir', filters?.sortDir || 'asc')

  const str = searchParams.toString()
  const qs = str ? `?${str}` : ''
  return qs
}

export const useFetchSatpenStatsByParentWilayah = (
  parentWilayah: string,
  filters: DataIndukFiltersParams,
  options: {
    enabled: boolean
  }
): UseQueryResult<SatpenStatsByParentWilayahResponse, Error> => {
  const qs = generateDataIndukQueryString(filters)

  return useQuery<SatpenStatsByParentWilayahResponse, Error>({
    queryKey: [`satpen-descendants`, parentWilayah, filters],
    queryFn: async (): Promise<SatpenStatsByParentWilayahResponse> =>
      fetchOnClient<SatpenStatsByParentWilayahResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/statistics/${parentWilayah}/descendants${qs}`
      ),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 0,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useFetchSatpenStats = (
  kodeWilayah: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<SatpenStatsResponse, Error> => {
  return useQuery<SatpenStatsResponse, Error>({
    queryKey: [`satpen-stats`, kodeWilayah],
    queryFn: async (): Promise<SatpenStatsResponse> =>
      fetchOnClient<SatpenStatsResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/statistics/${kodeWilayah}`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useWilayahAncestors = (
  kodeWilayah: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<DistrictAncestorsResponse, Error> => {
  return useQuery<DistrictAncestorsResponse, Error>({
    queryKey: [`satpen-breadcrumbs`, kodeWilayah],
    queryFn: async (): Promise<DistrictAncestorsResponse> =>
      fetchOnClient<DistrictAncestorsResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/districts/${kodeWilayah}/ancestors`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useFetchDataIndukFilters = (options: {
  enabled: boolean
}): UseQueryResult<DataIndukFiltersResponse, Error> => {
  return useQuery<DataIndukFiltersResponse, Error>({
    queryKey: [`satpen-filters`],
    queryFn: async (): Promise<DataIndukFiltersResponse> =>
      fetchOnClient<DataIndukFiltersResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/filters`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useFetchListOfSatpenByKecamatan = (
  offset: number = 0,
  filters: DataIndukFiltersWithKeywordParams,
  options: {
    enabled: boolean
  }
): UseQueryResult<SatpenListResponse, Error> => {
  const qs = generateDataIndukQueryString(filters)

  return useQuery<SatpenListResponse, Error>({
    queryKey: [`satpen-search`, offset, filters],
    queryFn: async (): Promise<SatpenListResponse> =>
      fetchOnClient<SatpenListResponse>(
        `${ENDPOINT.DATA_INDUK_SATPEN}/search${qs}${
          qs ? '&' : '?'
        }limit=20&offset=${offset}`
      ),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 0,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useMutateDownloadDataInduk = (): UseMutationResult<
  Blob,
  Error,
  DataIndukDownloadParams
> => {
  const mutateFn: MutationFunction<Blob, DataIndukDownloadParams> = (
    filters: DataIndukDownloadParams
  ): Promise<Blob> => {
    const qs = generateDataIndukQueryString(filters)

    return fetchGetBlob(
      `${ENDPOINT.DATA_INDUK_SATPEN}/download${qs}&format=${filters.format || 'csv'}`
    )
  }

  return useMutation<Blob, Error, DataIndukDownloadParams>({
    mutationKey: ['satpen-download'],
    mutationFn: mutateFn,
  })
}
